/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  AUTO_QUALITY_LEVEL_DEFINITION,
  ChromecastEventHandlers,
  ChromecastPlayerState,
  DefaultPlayerState,
  PlaybackState,
} from "./types";

const NOOP = () => {};

let castInitPromise: Promise<void> | undefined;
let configuredReceiverAppId: string | undefined;

export function setCastReceiverAppId(id: string | undefined) {
  configuredReceiverAppId = id;
}

export function initializeCast() {
  if (castInitPromise) {
    return castInitPromise;
  }

  return (castInitPromise = new Promise<void>((resolve, reject) => {
    window["__onGCastApiAvailable"] = (isAvailable) => {
      if (isAvailable) {
        if (!configuredReceiverAppId) {
          reject(new Error("[Cast] no receiver application id configured"));
          return;
        }
        cast.framework.CastContext.getInstance().setOptions({
          receiverApplicationId: configuredReceiverAppId,
          autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
        });
        resolve();
      } else {
        reject(new Error("[Cast] not available"));
      }
    };

    const castScript = document.createElement("script");
    castScript.src =
      "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
    document.body.appendChild(castScript);
  }));
}

// We can only create a single instance of the player & playerController
// subsequent creations of these will not receive events from the Cast SDK.
let player: cast.framework.RemotePlayer,
  playerController: cast.framework.RemotePlayerController;

export class CastSender {
  private state = { ...DefaultPlayerState };
  private eventHandlers: ChromecastEventHandlers;

  private session?: cast.framework.CastSession;
  private player?: cast.framework.RemotePlayer;
  private playerController?: cast.framework.RemotePlayerController;
  private lastContentId?: string;

  constructor(eventHandlers: ChromecastEventHandlers) {
    this.eventHandlers = eventHandlers;
    this.onAnyChange = this.onAnyChange.bind(this);
    this.onVolumeLevelChange = this.onVolumeLevelChange.bind(this);
    this.onMuteChange = this.onMuteChange.bind(this);
    this.onSessionStateChanged = this.onSessionStateChanged.bind(this);
    this.onCastStateChanged = this.onCastStateChanged.bind(this);

    initializeCast().then(
      () => {
        this.player = player = player || new cast.framework.RemotePlayer();
        this.playerController = playerController =
          playerController ||
          new cast.framework.RemotePlayerController(this.player);

        // Manually set state and dispatch events that we're connected
        // If the browser wasn't reloaded the framework won't automatically reconnect
        const context = cast.framework.CastContext.getInstance();

        this.onCastStateChanged({
          type: "",
          castState: context.getCastState(),
        });

        if (context.getCastState() === cast.framework.CastState.CONNECTED) {
          const session = context.getCurrentSession();
          if (session) {
            this.updateStateFromRemotePlayer();
            this.onConnected(session, true);
          }
        }

        this.setupListeners();
      },
      (err) => console.warn(`[Cast] ${err}`),
    );
  }

  private setState(newState: Partial<ChromecastPlayerState>) {
    const oldState = this.state;
    this.state = {
      ...oldState,
      ...newState,
    };
    this.eventHandlers?.onStateChanged?.(this.state, oldState);
  }

  private getStateFromPlayerState(
    state: chrome.cast.media.PlayerState | null,
  ): PlaybackState {
    switch (state) {
      case chrome.cast.media.PlayerState.BUFFERING:
        return PlaybackState.BUFFERING;
      case chrome.cast.media.PlayerState.PAUSED:
        return PlaybackState.PAUSED;
      case chrome.cast.media.PlayerState.PLAYING:
        return PlaybackState.PLAYING;
      case chrome.cast.media.PlayerState.IDLE:
      default:
        if (this.session && this.state.playbackState !== PlaybackState.IDLE) {
          return PlaybackState.ENDED;
        }
        return PlaybackState.IDLE;
    }
  }

  private onConnected(session: cast.framework.CastSession, isResumed: boolean) {
    this.session = session;
    this.eventHandlers?.onConnected?.(isResumed);
    this.setState({
      isMuted: this.player?.isMuted,
    });
  }

  private onDisconnected() {
    this.session = undefined;
    this.eventHandlers?.onDisconnected?.();
    this.setState({
      playbackState: PlaybackState.IDLE,
    });
  }

  getState() {
    return this.state;
  }

  isConnected() {
    return !!this.session;
  }

  isAvailable() {
    if (!window.cast) {
      return false;
    }

    return (
      cast.framework.CastContext.getInstance().getCastState() !==
      cast.framework.CastState.NO_DEVICES_AVAILABLE
    );
  }

  getContentId(): string | undefined {
    const mediaSession = this.session?.getMediaSession();
    if (mediaSession?.media?.contentId) {
      return mediaSession?.media?.contentId;
    }
    return this.lastContentId;
  }

  setupListeners() {
    const context = cast.framework.CastContext.getInstance();
    context.addEventListener(
      cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
      this.onSessionStateChanged,
    );

    context.addEventListener(
      cast.framework.CastContextEventType.CAST_STATE_CHANGED,
      this.onCastStateChanged,
    );

    this.playerController?.addEventListener(
      cast.framework.RemotePlayerEventType.ANY_CHANGE,
      this.onAnyChange,
    );

    this.playerController?.addEventListener(
      cast.framework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
      this.onVolumeLevelChange,
    );

    this.playerController?.addEventListener(
      cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED,
      this.onMuteChange,
    );
  }

  updateLastContentId() {
    const contentId = this.getContentId();
    if (contentId) {
      this.lastContentId = contentId;
    }
  }

  updateStateFromRemotePlayer() {
    if (!this.player) {
      return;
    }
    const {
      playerState,
      currentTime,
      duration,
      volumeLevel,
      isMuted,
      liveSeekableRange,
    } = this.player;

    const playbackState = this.getStateFromPlayerState(playerState);

    // @ts-ignore `startAbsoluteTime` is missing in the type definition
    const startTimeLive = this.player.mediaInfo?.startAbsoluteTime;

    this.setState({
      isLive: !!liveSeekableRange,
      currentTime,
      utcCurrentTime: startTimeLive ? startTimeLive + currentTime * 1000 : 0,
      duration,
      utcDuration: startTimeLive ? startTimeLive + duration : 0,
      seekable: {
        start: liveSeekableRange?.start ?? 0,
        end: liveSeekableRange?.end ?? duration,
      },
      playbackState,
      hasStarted:
        !this.state.hasStarted && playbackState === PlaybackState.PLAYING
          ? true
          : this.state.hasStarted,
      volume: volumeLevel,
      isMuted,
      audioTrack: this.getAudioTrack(),
      audioTracks: this.getAudioTracks(),
      subtitleTrack: this.getSubtitleTrack(),
      subtitleTracks: this.getSubtitleTracks(),
      isSeekable: this.player.canSeek,
      isAtLiveEdge:
        liveSeekableRange?.end !== undefined
          ? currentTime > liveSeekableRange.end - 30
          : false,
    });
  }

  private getTrackById(trackId: number) {
    return this.session
      ?.getMediaSession()
      ?.media?.tracks?.find((track) => track.trackId === trackId);
  }

  private getActiveTrackIds(): number[] {
    return this.session?.getMediaSession()?.activeTrackIds ?? [];
  }

  private getActiveAudioTrack(): chrome.cast.media.Track | undefined {
    const mediaSession = this.session?.getMediaSession();
    if (mediaSession) {
      return this.getInternalAudioTracks().find(
        (track) => mediaSession.activeTrackIds?.includes(track.trackId),
      );
    }
  }

  private getAudioTrackById(id: string | number) {
    return this.getInternalAudioTracks().find(
      (track) => track.trackId === Number(id),
    );
  }

  private getInternalAudioTracks(): chrome.cast.media.Track[] {
    return (
      this.player?.mediaInfo?.tracks?.filter(
        (track: chrome.cast.media.Track) =>
          track.type === chrome.cast.media.TrackType.AUDIO,
      ) ?? []
    );
  }

  private getActiveTextTrack(): chrome.cast.media.Track | undefined {
    const mediaSession = this.session?.getMediaSession();
    if (mediaSession) {
      return this.getTextTracks().find(
        (track) => mediaSession.activeTrackIds?.includes(track.trackId),
      );
    }
  }

  private getTextTrackById(id: string | number) {
    return this.getTextTracks().find((track) => track.trackId === Number(id));
  }

  private getTextTracks(): chrome.cast.media.Track[] {
    return (
      this.player?.mediaInfo?.tracks?.filter(
        (track: chrome.cast.media.Track) =>
          track.type === chrome.cast.media.TrackType.TEXT,
      ) ?? []
    );
  }

  onCastStateChanged({ castState }: cast.framework.CastStateEventData) {
    if (castState === cast.framework.CastState.NO_DEVICES_AVAILABLE) {
      this.eventHandlers?.onAvailabilityChange?.(false);
    } else {
      this.eventHandlers?.onAvailabilityChange?.(true);
    }
  }

  onSessionStateChanged({
    session,
    sessionState,
  }: cast.framework.SessionStateEventData) {
    switch (sessionState) {
      case cast.framework.SessionState.SESSION_STARTED:
      case cast.framework.SessionState.SESSION_RESUMED: {
        const isResumed =
          sessionState === cast.framework.SessionState.SESSION_RESUMED;
        this.onConnected(session, isResumed);
        break;
      }
      case cast.framework.SessionState.SESSION_ENDED:
        this.onDisconnected();
        break;
    }
  }

  onAnyChange() {
    this.updateLastContentId();
    this.updateStateFromRemotePlayer();
  }

  onVolumeLevelChange() {
    this.eventHandlers?.onVolumeLevelChange?.();
  }

  onMuteChange(value: any) {
    this.eventHandlers?.onMuteChanged?.(Boolean(value));
  }

  load({
    source,
    startTime,
    sessionToken,
  }: {
    source: string;
    startTime?: number;
    sessionToken?: string;
  }) {
    const mediaInfo = new chrome.cast.media.MediaInfo(source, "");
    const isAsset = !source.includes("://");
    if (!isAsset) {
      Object.assign(mediaInfo, { contentUrl: source });
    }
    const request = new chrome.cast.media.LoadRequest(mediaInfo);

    if (startTime) {
      request.currentTime = startTime;
    }

    if (isAsset) {
      Object.assign(request, { credentials: sessionToken });
    }

    return this.loadRequest(request);
  }

  private async loadRequest(
    request: chrome.cast.media.LoadRequest,
  ): Promise<void | chrome.cast.ErrorCode> {
    const castSession =
      cast.framework.CastContext.getInstance().getCurrentSession();

    if (!castSession) {
      const error = new Error("[Cast] Failed to create cast session");
      return Promise.reject(error);
    }

    const device = castSession.getCastDevice();
    const deviceId = `${device?.friendlyName || "Chromecast"}`;
    request.media.customData = { deviceId };

    return castSession
      .loadMedia(request)
      .catch((err: chrome.cast.ErrorCode) => {
        if (err === chrome.cast.ErrorCode.TIMEOUT) {
          // ignore timeout since that can happen even if everything is working fine
          return;
        }
        console.error("[CastSender]", err);
        this.eventHandlers?.onError?.(`Failed to load media: ${err}`);
      });
  }

  isPlaying() {
    return this.state.playbackState === PlaybackState.PLAYING;
  }

  isLive() {
    return this.state.isLive;
  }

  play() {
    if (this.player?.isPaused) {
      this.playerController?.playOrPause();
    }
    return Promise.resolve();
  }

  pause() {
    if (!this.player?.isPaused) {
      this.playerController?.playOrPause();
    }
  }

  stop() {
    this.session?.endSession(true);
  }

  mute() {
    if (this.player && this.playerController && !this.player.isMuted) {
      this.playerController.muteOrUnmute();
    }
  }

  unmute() {
    if (this.player && this.playerController && this.player.isMuted) {
      this.playerController.muteOrUnmute();
    }
  }

  getCurrentTime() {
    return this.state.currentTime;
  }

  getDuration() {
    return this.state.duration;
  }

  getUTCCurrentTime() {
    return this.state.utcCurrentTime;
  }

  getUTCDuration() {
    return this.state.utcDuration;
  }

  getSeekable() {
    if (this.player?.liveSeekableRange) {
      return {
        start: this.player.liveSeekableRange.start ?? 0,
        end: this.player.liveSeekableRange.end ?? 0,
      };
    }
    return {
      start: 0,
      end: this.player?.duration ?? 0,
    };
  }

  getDeviceName() {
    if (!this.session) {
      return "";
    }

    const device = this.session.getCastDevice();
    return device ? device.friendlyName : "Chromecast";
  }

  scrub(change: number) {
    if (!this.player?.canSeek) {
      return;
    }
    this.seekTo(this.player.currentTime + change);
  }

  seekTo(time: number) {
    if (!this.player?.canSeek || !this.playerController) {
      return;
    }
    this.player.currentTime = time;
    this.playerController.seek();
  }

  seekToLive() {
    if (
      this.playerController &&
      this.player?.liveSeekableRange?.end !== undefined
    ) {
      this.player.currentTime = this.player.liveSeekableRange.end;
      this.playerController.seek();
    }
  }

  setAudioTrack(track: chrome.cast.media.Track) {
    const internalTrack = this.getAudioTrackById(track.trackId);
    if (!internalTrack) {
      return;
    }

    const media = this.session?.getMediaSession();
    const activeTrackIds = this.getActiveTrackIds();
    const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
      activeTrackIds.map((id) => {
        if (this.getTrackById(id)?.type === chrome.cast.media.TrackType.AUDIO) {
          return internalTrack.trackId;
        }
        return id;
      }),
    );
    media?.editTracksInfo(tracksInfoRequest, NOOP, NOOP);
  }

  getAudioTrack() {
    const track = this.getActiveAudioTrack();
    return track;
  }

  getAudioTracks() {
    return this.getInternalAudioTracks();
  }

  setSubtitleTrack(track: chrome.cast.media.Track) {
    const mediaSession = this.session?.getMediaSession();
    if (!mediaSession) {
      return;
    }

    let activeTrackIds = this.getActiveTrackIds();
    if (track) {
      const internalTrack = this.getTextTrackById(track.trackId);
      if (!internalTrack) {
        return;
      }

      if (this.getActiveTextTrack()) {
        activeTrackIds = activeTrackIds.map((id) => {
          if (
            this.getTrackById(id)?.type === chrome.cast.media.TrackType.TEXT
          ) {
            return internalTrack.trackId;
          }
          return id;
        });
      } else {
        activeTrackIds.push(internalTrack.trackId);
      }
      const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
        activeTrackIds,
      );
      mediaSession.editTracksInfo(tracksInfoRequest, NOOP, NOOP);
    } else {
      const tracksInfoRequest = new chrome.cast.media.EditTracksInfoRequest(
        activeTrackIds.filter(
          (id) =>
            this.getTrackById(id)?.type !== chrome.cast.media.TrackType.TEXT,
        ),
      );
      mediaSession.editTracksInfo(tracksInfoRequest, NOOP, NOOP);
    }
  }

  getSubtitleTrack() {
    const track = this.getActiveTextTrack();
    return track;
  }

  getSubtitleTracks() {
    return this.getTextTracks();
  }

  setQualityLevel() {}

  getQualityLevel() {
    return AUTO_QUALITY_LEVEL_DEFINITION;
  }

  getQualityLevels() {
    return [AUTO_QUALITY_LEVEL_DEFINITION];
  }

  getVolume() {
    return +(this.player?.volumeLevel.toFixed(2) || 0);
  }

  setVolume(percentage: number) {
    if (!this.player) {
      return;
    }

    let volume = this.getVolume();
    if (volume === undefined || this.player.volumeLevel === percentage) {
      return;
    }

    volume = percentage;

    this.player.volumeLevel = volume;
    this.playerController?.setVolumeLevel();
  }

  setMuted(muted: boolean): void {
    if (this.playerController && this.player?.isMuted !== muted) {
      this.playerController.muteOrUnmute();
    }
  }

  isMuted(): boolean {
    return this.player?.isMuted || false;
  }

  toggleMuted() {
    this.playerController?.muteOrUnmute();
  }

  destroy({
    retainCastSession = false,
  }: {
    retainCastSession?: boolean;
  } = {}): void {
    if (window.cast) {
      const context = cast.framework.CastContext.getInstance();
      context.removeEventListener(
        cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
        this.onSessionStateChanged,
      );

      context.removeEventListener(
        cast.framework.CastContextEventType.CAST_STATE_CHANGED,
        this.onCastStateChanged,
      );

      this.playerController?.removeEventListener(
        cast.framework.RemotePlayerEventType.ANY_CHANGE,
        this.onAnyChange,
      );

      this.playerController?.removeEventListener(
        cast.framework.RemotePlayerEventType.VOLUME_LEVEL_CHANGED,
        this.onVolumeLevelChange,
      );

      this.playerController?.removeEventListener(
        cast.framework.RemotePlayerEventType.IS_MUTED_CHANGED,
        this.onMuteChange,
      );

      if (retainCastSession === false) {
        context.endCurrentSession(true);
      }
    }
  }
}
